import { QueryKey, UseInfiniteQueryOptions, useQueryClient } from '@tanstack/react-query';
import { DocumentType } from '@steelbuy/ts-shared';
import { ResponseModel } from '../../utils/types';
import { usePaginatedQuery } from '../../utils/usePaginatedQuery';

export type BulkUploadDocument = {
    bulkId: string;
    documentId: string;
    fileName: string;
    size: number;
};

export const BULK_UPLOAD_CERTS_KEY = 'bulk-upload-certs';
export const BULK_UPLOAD_PICTURES_KEY = 'bulk-upload-pictures';

// removes all but first page of cache
const trim = (queryClient: ReturnType<typeof useQueryClient>, queryKey: QueryKey) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData?.pages) return oldData;

        return {
            ...oldData,
            pages: oldData.pages.slice(0, 1),
            pageParams: oldData.pageParams.slice(0, 1),
        };
    });
};

export const useDraftBulkUploadDocuments = ({
    bulkId,
    queryOptions,
}: {
    bulkId?: string;
    queryOptions?: Omit<
        UseInfiniteQueryOptions<ResponseModel<BulkUploadDocument[]>>,
        'queryKey' | 'queryFn' | 'getNextPageParam'
    >;
}) => {
    const queryClient = useQueryClient();
    const url = `/api/universal/bulk-listing-drafts/${bulkId}/documents`;
    const options = {
        enabled: !!bulkId && queryOptions?.enabled !== false,
        refetchOnMount: false,
        staleTime: Infinity,
        cacheTime: Infinity,
        ...queryOptions,
    };
    const certificatesQuery = usePaginatedQuery({
        url,
        queryKey: [BULK_UPLOAD_CERTS_KEY, bulkId],
        requestOptions: {
            params: {
                type: DocumentType.ORIGINAL,
            },
        },
        queryOptions: options,
    });

    const picturesQuery = usePaginatedQuery({
        url,
        queryKey: [BULK_UPLOAD_PICTURES_KEY, bulkId],
        requestOptions: {
            params: {
                type: DocumentType.PICTURE,
            },
        },
        queryOptions: options,
    });

    return {
        certificatesQuery: {
            ...certificatesQuery,
            trim: () => trim(queryClient, [BULK_UPLOAD_CERTS_KEY, bulkId]),
        },
        picturesQuery: {
            ...picturesQuery,
            trim: () => trim(queryClient, [BULK_UPLOAD_PICTURES_KEY, bulkId]),
        },
        isLoading: certificatesQuery.isLoading || picturesQuery.isLoading,
        isSuccess: certificatesQuery.isSuccess && picturesQuery.isSuccess,
    };
};
